import React, { createContext, useState, useContext, ReactNode } from "react";
import resourceApiService from "../apiService/resource/resourceApiService";
import { Resource } from "../pages/RoleManagementPage/types";

interface ResourceContextType {
  loading: boolean;
  error: string;
  resources: Resource[];
  selectedResource: Resource | null;

  addResource: (resource: Partial<Resource>) => Promise<void>;
  deleteResource: (resourceId: string) => Promise<void>;
  editResource: (resourceId: string, data: Partial<Resource>) => Promise<void>;
  getResource: (resourceId: string) => Promise<void>;
  getAllResources: () => Promise<void>;
  attachRolesAndScopes: (resourceId: string, roles: string[], scopes: string[]) => Promise<void>;
  getResourceRolesAndScopes: (resourceId: string) => Promise<{ roles: string[]; scopes: string[] }>;
}

export const ResourceContext = createContext<ResourceContextType | undefined>(undefined);

export const ResourceProvider = ({ children }: { children: ReactNode }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [resources, setResources] = useState<Resource[]>([]);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);

  const addResource = async (resource: Partial<Resource>) => {
    setLoading(true);
    setError("");
    try {
      await resourceApiService.addResource(resource);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to add resource");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteResource = async (resourceId: string) => {
    setLoading(true);
    setError("");
    try {
      await resourceApiService.deleteResource(resourceId);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to delete resource");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const editResource = async (resourceId: string, data: Partial<Resource>) => {
    setLoading(true);
    setError("");
    try {
      await resourceApiService.editResource(resourceId, data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to edit resource");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getResource = async (resourceId: string) => {
    setLoading(true);
    setError("");
    try {
      const response = await resourceApiService.getResource(resourceId);
      setSelectedResource(response.body);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to fetch resource");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getAllResources = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await resourceApiService.getAllResources();
      setResources(response.body);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to fetch resources");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const attachRolesAndScopes = async (resourceId: string, roles: string[], scopes: string[]) => {
    setLoading(true);
    setError("");
    try {
      await resourceApiService.attachRolesAndScopes(resourceId, { roles, scopes });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to attach roles and scopes");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getResourceRolesAndScopes = async (resourceId: string) => {
    setLoading(true);
    setError("");
    try {
      const response = await resourceApiService.getResourceRolesAndScopes(resourceId);
      return response.body;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to fetch resource roles and scopes");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    loading,
    error,
    resources,
    selectedResource,
    addResource,
    deleteResource,
    editResource,
    getResource,
    getAllResources,
    attachRolesAndScopes,
    getResourceRolesAndScopes,
  };

  return <ResourceContext.Provider value={value}>{children}</ResourceContext.Provider>;
};

export const useResource = (): ResourceContextType => {
  const context = useContext(ResourceContext);
  if (context === undefined) {
    throw new Error("useResource must be used within a ResourceProvider");
  }
  return context;
}; 